/**
 * Iter 96 — Moteur de landing pages géolocalisées.
 * Chaque marché a un contenu RÉELLEMENT unique (pas une traduction) :
 * Title, meta description, H1, angles marché, FAQ, JSON-LD, devise locale.
 * Règle : aucune redirection IP forcée — suggestion discrète uniquement.
 */

export const SITE_URL = 'https://copilotebusiness.ai';

export const PLAN_PRICES = { starter: 149, growth: 279, pro: 449 };

export const MARKETS = {
  'ch-fr': {
    slug: 'ch-fr',
    path: '/ch-fr',
    hreflang: 'fr-CH',
    extraHreflangs: [],
    locale: 'fr',
    ogLocale: 'fr_CH',
    currency: 'CHF',
    currencyLocale: 'fr-CH',
    country: 'CH',
    countryLabel: 'Suisse romande',
    flag: '🇨🇭',
    title: 'Logiciel restaurant Suisse romande — Copilote AI Business (CHF)',
    description:
      "Le copilote IA des restaurateurs romands : détectez les pertes de revenus, réactivez vos clients et pilotez vos avis Google. Essai gratuit 14 jours, prix en CHF.",
    keywords: 'logiciel restaurant suisse, logiciel gestion restaurant suisse romande, marketing restaurant genève lausanne, fidélisation clients restaurant CHF',
    badge: 'Conçu en Suisse, pour les restaurateurs suisses',
    h1: 'Le copilote IA des restaurateurs de Suisse romande',
    heroSub:
      "De Genève à Sion, les marges des restaurants romands sont parmi les plus serrées d'Europe : loyers élevés, salaires en CHF, clientèle exigeante. Copilote AI Business détecte chaque semaine où votre établissement perd du chiffre d'affaires — clients qui ne reviennent plus, avis sans réponse, services creux — et prépare les actions pour le récupérer.",
    ctaPrimary: "Démarrer l'essai gratuit (14 jours)",
    ctaSecondary: 'Voir la démo (90 s)',
    ctaScore: 'Obtenir mon score gratuit',
    ctaMicrocopy: 'Sans carte bancaire · Résiliable en un clic · Prix en CHF',
    painsTitle: 'Où un restaurant romand perd-il du chiffre chaque mois ?',
    painsSub: 'Estimations types pour un établissement de 40 à 80 couverts en Suisse romande.',
    pains: [
      { loss: 2500, title: 'Habitués perdus sans signal', desc: "Un client lausannois qui ne revient pas après 30 jours est rarement recontacté. Le copilote le détecte et prépare la relance." },
      { loss: 1200, title: 'Avis Google et TheFork sans réponse', desc: "En Suisse romande, la note Google décide du choix entre deux terrasses. Chaque avis négatif ignoré coûte des réservations." },
      { loss: 3500, title: 'Services creux non anticipés', desc: 'Mardis midi vides, basse saison lémanique : sans données, impossible de lancer la bonne offre au bon moment.' },
    ],
    featuresTitle: 'Ce que le copilote fait pour votre établissement',
    features: [
      { title: 'Audit hebdomadaire du manque à gagner', desc: 'Chaque lundi, un diagnostic chiffré en CHF : où vous perdez, combien, et quelle action lancer.' },
      { title: 'Réactivation automatique des habitués', desc: 'Messages de relance prêts à valider, rédigés en français, adaptés à votre carte et à votre ton.' },
      { title: 'Pilotage des avis clients', desc: 'Brouillons de réponse générés par IA pour chaque avis — vous gardez toujours la validation finale.' },
      { title: 'Campagnes locales prêtes à envoyer', desc: 'Offres ciblées pour remplir les services creux, calibrées sur la saisonnalité romande.' },
    ],
    platformsNote:
      "Vos clients vous cherchent sur Google, TheFork et TripAdvisor : le copilote vous aide à y rester visible et bien noté — sans remplacer vos outils existants.",
    faqTitle: 'Questions fréquentes des restaurateurs romands',
    faqs: [
      { q: 'Quel est le meilleur logiciel de gestion marketing pour un restaurant en Suisse romande ?', a: "Copilote AI Business est conçu pour les restaurants suisses : prix en CHF, interface en français, et un moteur IA qui détecte chaque semaine les pertes de revenus (clients inactifs, avis sans réponse, services creux) puis prépare les actions correctives. Vous validez, il exécute." },
      { q: 'Combien coûte Copilote AI Business en Suisse ?', a: 'Trois formules en CHF : Starter à 149 CHF/mois, Growth à 279 CHF/mois et Pro à 449 CHF/mois, après un essai gratuit de 14 jours sans carte bancaire. Les prix sont fixes, sans conversion de devise.' },
      { q: 'Mes données clients sont-elles traitées conformément à la nLPD ?', a: "Oui. Les données sont traitées conformément à la nouvelle Loi fédérale sur la protection des données (nLPD) et au RGPD : vous restez propriétaire de vos données clients et pouvez les exporter ou les supprimer à tout moment." },
      { q: "Est-ce adapté à un petit établissement de 30 couverts à Neuchâtel ou Fribourg ?", a: "Oui. La formule Starter est pensée pour les établissements indépendants : l'AI Digest hebdomadaire et la réactivation clients fonctionnent dès les premières dizaines de clients enregistrés." },
      { q: 'Dois-je remplacer ma caisse ou mon système de réservation ?', a: "Non. Copilote AI Business complète vos outils actuels : il analyse vos données clients et votre visibilité en ligne, puis vous prépare des actions concrètes. Aucune migration n'est nécessaire pour commencer." },
    ],
    pricingTitle: 'Prix en francs suisses, sans surprise',
    pricingSub: 'Essai gratuit 14 jours · Sans engagement · TVA suisse incluse dans les prix affichés',
    planBullets: {
      starter: ['Audit hebdomadaire en CHF', "Jusqu'à 500 clients suivis", 'Réponses aux avis assistées par IA'],
      growth: ['Tout Starter', 'Campagnes de réactivation illimitées', 'Analyse de la concurrence locale'],
      pro: ['Tout Growth', 'Multi-établissements', 'Accompagnement prioritaire'],
    },
    otherMarketsTitle: 'Copilote AI Business dans votre région',
    finalCtaTitle: 'Reprenez le contrôle de votre chiffre d’affaires',
    finalCtaSub: "Rejoignez les restaurateurs romands qui savent chaque lundi où agir. L'essai est gratuit, la première analyse aussi.",
  },

  'ch-de': {
    slug: 'ch-de',
    path: '/ch-de',
    hreflang: 'de-CH',
    extraHreflangs: [],
    locale: 'de',
    ogLocale: 'de_CH',
    currency: 'CHF',
    currencyLocale: 'de-CH',
    country: 'CH',
    countryLabel: 'Deutschschweiz',
    flag: '🇨🇭',
    title: 'Restaurant-Software Schweiz — Copilote AI Business (CHF)',
    description:
      'Der KI-Copilot für Deutschschweizer Gastronomen: Umsatzverluste erkennen, Stammgäste reaktivieren, Google-Bewertungen im Griff. 14 Tage gratis testen, Preise in CHF.',
    keywords: 'restaurant software schweiz, gastro software zürich bern basel, stammgäste marketing restaurant, google bewertungen restaurant schweiz',
    badge: 'Für die Deutschschweizer Gastronomie entwickelt',
    h1: 'Der KI-Copilot für Gastronomen in der Deutschschweiz',
    heroSub:
      'Fachkräftemangel, steigende Einkaufspreise, leere Tische am Dienstagmittag: Zwischen Zürich, Bern und Basel bleibt Wirten keine Zeit für Marketing. Copilote AI Business analysiert jede Woche, wo Ihr Betrieb Umsatz liegen lässt — und bereitet die passende Massnahme vor. Sie geben nur noch frei.',
    ctaPrimary: 'Jetzt 14 Tage gratis testen',
    ctaSecondary: 'Demo ansehen (90 s)',
    ctaScore: 'Meinen Gratis-Score erhalten',
    ctaMicrocopy: 'Ohne Kreditkarte · Jederzeit kündbar · Preise in CHF',
    painsTitle: 'Wo verliert ein Deutschschweizer Restaurant jeden Monat Geld?',
    painsSub: 'Typische Werte für einen Betrieb mit 40–80 Plätzen.',
    pains: [
      { loss: 2500, title: 'Stammgäste verschwinden unbemerkt', desc: 'Wer nach 30 Tagen nicht zurückkommt, ist meist verloren. Der Copilot erkennt es und bereitet die Rückgewinnung vor.' },
      { loss: 1200, title: 'Unbeantwortete Google-Bewertungen', desc: 'In der Deutschschweiz entscheidet die Google-Note über den Mittagsgast. Jede ignorierte Kritik kostet Reservationen.' },
      { loss: 3500, title: 'Leere Services ohne Vorwarnung', desc: 'Ruhige Wochentage und Zwischensaison: Ohne Datenbasis kommt das richtige Angebot immer zu spät.' },
    ],
    featuresTitle: 'Das übernimmt der Copilot für Ihren Betrieb',
    features: [
      { title: 'Wöchentliches Umsatz-Audit', desc: 'Jeden Montag eine klare Analyse in CHF: wo Sie verlieren, wie viel, und welche Massnahme sich lohnt.' },
      { title: 'Automatische Stammgast-Reaktivierung', desc: 'Fertige Rückgewinnungs-Nachrichten auf Deutsch — Sie prüfen und geben mit einem Klick frei.' },
      { title: 'Bewertungs-Management', desc: 'KI-Antwortentwürfe für jede Bewertung, im Ton Ihres Hauses. Die Freigabe bleibt immer bei Ihnen.' },
      { title: 'Lokale Kampagnen auf Knopfdruck', desc: 'Gezielte Aktionen für schwache Services — abgestimmt auf Saison und Quartier.' },
    ],
    platformsNote:
      'Ihre Gäste suchen Sie auf Google, TheFork und TripAdvisor: Der Copilot hilft Ihnen, dort sichtbar und gut bewertet zu bleiben — ohne Ihre bestehenden Tools zu ersetzen.',
    faqTitle: 'Häufige Fragen von Schweizer Gastronomen',
    faqs: [
      { q: 'Welche Restaurant-Software eignet sich für die Schweiz?', a: 'Copilote AI Business wurde für Schweizer Betriebe entwickelt: Preise in CHF, Oberfläche auf Deutsch, und eine KI, die wöchentlich Umsatzverluste erkennt (inaktive Gäste, offene Bewertungen, schwache Services) und die passenden Massnahmen vorbereitet.' },
      { q: 'Was kostet Copilote AI Business in der Schweiz?', a: 'Drei Pakete in CHF: Starter für 149 CHF/Monat, Growth für 279 CHF/Monat und Pro für 449 CHF/Monat — nach 14 Tagen Gratis-Test ohne Kreditkarte. Keine Währungsumrechnung, keine versteckten Kosten.' },
      { q: 'Ist die Lösung nDSG- und DSGVO-konform?', a: 'Ja. Ihre Gästedaten werden gemäss dem neuen Schweizer Datenschutzgesetz (nDSG) und der DSGVO verarbeitet. Sie bleiben Eigentümer Ihrer Daten und können sie jederzeit exportieren oder löschen.' },
      { q: 'Lohnt sich das für ein kleines Lokal in Luzern oder Winterthur?', a: 'Ja. Das Starter-Paket ist für unabhängige Betriebe gedacht: Das Wochen-Audit und die Gäste-Reaktivierung funktionieren bereits ab wenigen Dutzend erfassten Gästen.' },
      { q: 'Muss ich mein Kassensystem oder Reservationstool ersetzen?', a: 'Nein. Der Copilot ergänzt Ihre bestehende Infrastruktur: Er analysiert Gästedaten und Online-Sichtbarkeit und bereitet konkrete Massnahmen vor. Für den Start ist keine Migration nötig.' },
    ],
    pricingTitle: 'Preise in Schweizer Franken — transparent',
    pricingSub: '14 Tage gratis testen · Monatlich kündbar · Schweizer MwSt. inbegriffen',
    planBullets: {
      starter: ['Wöchentliches Audit in CHF', 'Bis 500 erfasste Gäste', 'KI-Antworten auf Bewertungen'],
      growth: ['Alles aus Starter', 'Unbegrenzte Reaktivierungs-Kampagnen', 'Lokale Konkurrenz-Analyse'],
      pro: ['Alles aus Growth', 'Mehrere Standorte', 'Prioritäts-Support'],
    },
    otherMarketsTitle: 'Copilote AI Business in Ihrer Region',
    finalCtaTitle: 'Holen Sie sich die Kontrolle über Ihren Umsatz zurück',
    finalCtaSub: 'Wissen Sie jeden Montag, wo zu handeln ist. Der Test ist gratis — die erste Analyse auch.',
  },

  fr: {
    slug: 'fr',
    path: '/fr',
    hreflang: 'fr-FR',
    extraHreflangs: ['fr'],
    locale: 'fr',
    ogLocale: 'fr_FR',
    currency: 'EUR',
    currencyLocale: 'fr-FR',
    country: 'FR',
    countryLabel: 'France',
    flag: '🇫🇷',
    title: 'Logiciel restaurant France : IA marketing & fidélisation — Copilote',
    description:
      "Logiciel restaurant pour la France : détectez les pertes de CA, réactivez vos clients, répondez aux avis Google et TheFork. IA en français, prix en euros, essai 14 jours.",
    keywords: 'logiciel restaurant france, logiciel gestion marketing restaurant, fidélisation clients restaurant, réponse avis google restaurant, logiciel restaurateur indépendant',
    badge: 'Pensé pour les restaurateurs indépendants français',
    h1: 'Le logiciel restaurant qui récupère votre chiffre d’affaires en France',
    heroSub:
      "En France, un restaurant indépendant fait face à la concurrence la plus dense d'Europe : 175 000 établissements, des clients volatils et des plateformes qui captent la relation. Copilote AI Business analyse vos données chaque semaine, détecte où vous perdez du chiffre — no-shows, habitués envolés, avis sans réponse — et prépare les campagnes pour le reprendre.",
    ctaPrimary: "Commencer l'essai gratuit (14 jours)",
    ctaSecondary: 'Voir la démo (90 s)',
    ctaScore: 'Obtenir mon score gratuit',
    ctaMicrocopy: 'Sans carte bancaire · Sans engagement · Prix en euros',
    painsTitle: 'Où un restaurant français perd-il du chiffre chaque mois ?',
    painsSub: "Estimations types pour un indépendant de 40 à 80 couverts en France.",
    pains: [
      { loss: 2500, title: 'Clients fidèles jamais relancés', desc: "70 % des clients ne reviennent jamais après une première visite. Le copilote identifie ceux qui décrochent et prépare la relance SMS ou e-mail." },
      { loss: 1200, title: 'Avis Google et TheFork ignorés', desc: 'En France, 9 clients sur 10 lisent les avis avant de réserver. Chaque avis négatif sans réponse fait fuir des tables.' },
      { loss: 3500, title: 'Dépendance aux plateformes', desc: "Sans base clients travaillée, vous payez des commissions pour des clients qui pourraient réserver en direct." },
    ],
    featuresTitle: 'Ce que le copilote fait pour votre restaurant',
    features: [
      { title: 'Audit hebdomadaire du manque à gagner', desc: 'Chaque lundi, un diagnostic chiffré en euros : les fuites de CA détectées et les actions prêtes à valider.' },
      { title: 'Fidélisation et relance clients', desc: 'Campagnes de réactivation rédigées par IA en français, envoyées au bon moment, avec votre validation.' },
      { title: 'Gestion des avis clients', desc: "Brouillons de réponse pour chaque avis Google — professionnels, dans le ton de votre maison. Vous validez avant publication." },
      { title: 'Campagnes anti-creux', desc: 'Offres ciblées pour remplir les services faibles : déjeuners de semaine, débuts de soirée, basse saison.' },
    ],
    platformsNote:
      "Vos clients vous découvrent sur Google, TheFork et TripAdvisor et commandent via Deliveroo ou Uber Eats : le copilote vous aide à transformer cette audience en clients directs et fidèles.",
    faqTitle: 'Questions fréquentes des restaurateurs français',
    faqs: [
      { q: 'Quel est le meilleur logiciel de marketing pour un restaurant en France ?', a: "Copilote AI Business se distingue par son approche « copilote » : au lieu de vous donner des tableaux de bord à interpréter, l'IA détecte chaque semaine vos pertes de chiffre d'affaires (clients inactifs, avis sans réponse, services creux) et prépare les actions correctives. Vous validez, il exécute." },
      { q: 'Combien coûte le logiciel pour un restaurant en France ?', a: "Trois formules en euros : Starter à 149 €/mois, Growth à 279 €/mois et Pro à 449 €/mois, après 14 jours d'essai gratuit sans carte bancaire. Sans engagement de durée." },
      { q: 'Le logiciel est-il conforme au RGPD ?', a: 'Oui. Les données de vos clients sont traitées conformément au RGPD : consentement, droit à l’effacement et export de données sont intégrés. Vous restez seul propriétaire de votre base clients.' },
      { q: 'Est-ce adapté à un petit restaurant de quartier ?', a: "Oui. La formule Starter est conçue pour les indépendants : dès quelques dizaines de clients enregistrés, l'AI Digest hebdomadaire et les relances automatiques produisent des résultats mesurables." },
      { q: 'Faut-il changer de caisse ou de module de réservation ?', a: "Non. Copilote AI Business complète vos outils existants : il analyse votre base clients et votre visibilité en ligne, sans migration technique. Vous pouvez démarrer le jour même." },
    ],
    pricingTitle: 'Prix en euros, sans engagement',
    pricingSub: 'Essai gratuit 14 jours · Résiliable à tout moment · TVA incluse',
    planBullets: {
      starter: ['Audit hebdomadaire en €', "Jusqu'à 500 clients suivis", 'Réponses aux avis assistées par IA'],
      growth: ['Tout Starter', 'Campagnes de réactivation illimitées', 'Analyse de la concurrence locale'],
      pro: ['Tout Growth', 'Multi-établissements', 'Accompagnement prioritaire'],
    },
    otherMarketsTitle: 'Copilote AI Business dans votre pays',
    finalCtaTitle: 'Chaque semaine sans copilote, du chiffre s’évapore',
    finalCtaSub: "Testez gratuitement pendant 14 jours et découvrez dès le premier lundi où votre restaurant perd du chiffre d'affaires.",
  },

  de: {
    slug: 'de',
    path: '/de',
    hreflang: 'de-DE',
    extraHreflangs: ['de'],
    locale: 'de',
    ogLocale: 'de_DE',
    currency: 'EUR',
    currencyLocale: 'de-DE',
    country: 'DE',
    countryLabel: 'Deutschland',
    flag: '🇩🇪',
    title: 'Restaurant-Software Deutschland: KI-Marketing — Copilote AI Business',
    description:
      'Restaurant-Software für Deutschland: Umsatzverluste erkennen, Stammkunden zurückgewinnen, Google-Bewertungen professionell beantworten. KI auf Deutsch, Preise in Euro, 14 Tage gratis.',
    keywords: 'restaurant software deutschland, gastronomie marketing software, stammkunden gewinnen restaurant, google bewertungen beantworten gastronomie',
    badge: 'Für die deutsche Gastronomie entwickelt',
    h1: 'Die Restaurant-Software, die in Deutschland Umsatz zurückholt',
    heroSub:
      'Zwischen Personalmangel, Energiekosten und Lieferando-Kommissionen bleibt deutschen Gastronomen kaum Zeit fürs Marketing. Copilote AI Business analysiert Woche für Woche Ihre Daten, findet die Umsatzlecks — abgesprungene Stammkunden, unbeantwortete Bewertungen, schwache Schichten — und bereitet die Gegenmassnahme vor. Sie klicken nur noch auf „Freigeben“.',
    ctaPrimary: 'Jetzt 14 Tage gratis testen',
    ctaSecondary: 'Demo ansehen (90 s)',
    ctaScore: 'Meinen Gratis-Score erhalten',
    ctaMicrocopy: 'Ohne Kreditkarte · Monatlich kündbar · Preise in Euro',
    painsTitle: 'Wo verliert ein deutsches Restaurant jeden Monat Umsatz?',
    painsSub: 'Typische Werte für einen Betrieb mit 40–80 Plätzen in Deutschland.',
    pains: [
      { loss: 2500, title: 'Stammkunden ohne Wiederkehr', desc: 'Die meisten Gäste kommen nach dem ersten Besuch nie wieder. Der Copilot erkennt Absprünge und bereitet die Rückgewinnung vor.' },
      { loss: 1200, title: 'Google-Bewertungen bleiben offen', desc: 'In Deutschland ist die Google-Note das erste Auswahlkriterium. Unbeantwortete Kritik kostet jede Woche Gäste.' },
      { loss: 3500, title: 'Abhängigkeit von Lieferplattformen', desc: 'Ohne eigene Gästebasis zahlen Sie Kommissionen für Kunden, die auch direkt bestellen würden.' },
    ],
    featuresTitle: 'Das übernimmt der Copilot für Ihr Restaurant',
    features: [
      { title: 'Wöchentliches Umsatz-Audit', desc: 'Jeden Montag eine klare Analyse in Euro: erkannte Umsatzlecks und vorbereitete Massnahmen zur Freigabe.' },
      { title: 'Stammkunden-Rückgewinnung', desc: 'KI-getextete Reaktivierungs-Kampagnen auf Deutsch, zum richtigen Zeitpunkt — mit Ihrer Freigabe.' },
      { title: 'Bewertungs-Management', desc: 'Professionelle Antwortentwürfe für jede Google-Bewertung, im Ton Ihres Hauses. Veröffentlicht wird nur nach Ihrer Freigabe.' },
      { title: 'Kampagnen gegen schwache Schichten', desc: 'Gezielte Angebote für ruhige Wochentage und Nebensaison — datenbasiert statt Bauchgefühl.' },
    ],
    platformsNote:
      'Ihre Gäste finden Sie über Google, Lieferando und TripAdvisor: Der Copilot hilft Ihnen, aus dieser Reichweite direkte, wiederkehrende Gäste zu machen.',
    faqTitle: 'Häufige Fragen deutscher Gastronomen',
    faqs: [
      { q: 'Welche Restaurant-Software ist die beste für Deutschland?', a: 'Copilote AI Business verfolgt einen anderen Ansatz als klassische Dashboards: Die KI erkennt jede Woche konkrete Umsatzverluste (inaktive Stammkunden, offene Bewertungen, schwache Schichten) und bereitet die passende Massnahme vor. Sie behalten die volle Kontrolle über jede Freigabe.' },
      { q: 'Was kostet die Software in Deutschland?', a: 'Drei Pakete in Euro: Starter für 149 €/Monat, Growth für 279 €/Monat und Pro für 449 €/Monat — nach 14 Tagen kostenlosem Test ohne Kreditkarte. Monatlich kündbar.' },
      { q: 'Ist die Software DSGVO-konform?', a: 'Ja. Gästedaten werden DSGVO-konform verarbeitet: Einwilligung, Löschrecht und Datenexport sind integriert. Ihre Gästedatenbank gehört ausschliesslich Ihnen.' },
      { q: 'Eignet sich das für ein kleines Restaurant in Köln oder Leipzig?', a: 'Ja. Das Starter-Paket ist für unabhängige Betriebe konzipiert: Bereits ab einigen Dutzend erfassten Gästen liefert das Wochen-Audit messbare Ergebnisse.' },
      { q: 'Muss ich mein Kassensystem wechseln?', a: 'Nein. Der Copilot ergänzt Ihre bestehende Technik: Er analysiert Gästedaten und Online-Sichtbarkeit, ohne Migration. Sie können noch heute starten.' },
    ],
    pricingTitle: 'Preise in Euro — monatlich kündbar',
    pricingSub: '14 Tage gratis testen · Keine Einrichtungskosten im Test · MwSt. inbegriffen',
    planBullets: {
      starter: ['Wöchentliches Audit in €', 'Bis 500 erfasste Gäste', 'KI-Antworten auf Bewertungen'],
      growth: ['Alles aus Starter', 'Unbegrenzte Reaktivierungs-Kampagnen', 'Lokale Wettbewerbs-Analyse'],
      pro: ['Alles aus Growth', 'Mehrere Standorte', 'Prioritäts-Support'],
    },
    otherMarketsTitle: 'Copilote AI Business in Ihrem Land',
    finalCtaTitle: 'Jede Woche ohne Copilot kostet Umsatz',
    finalCtaSub: 'Testen Sie 14 Tage gratis — und sehen Sie am ersten Montag, wo Ihr Restaurant Geld liegen lässt.',
  },

  it: {
    slug: 'it',
    path: '/it',
    hreflang: 'it-IT',
    extraHreflangs: ['it'],
    locale: 'it',
    ogLocale: 'it_IT',
    currency: 'EUR',
    currencyLocale: 'it-IT',
    country: 'IT',
    countryLabel: 'Italia',
    flag: '🇮🇹',
    title: 'Software per ristoranti Italia: marketing con IA — Copilote',
    description:
      'Software per ristoranti in Italia: individua le perdite di fatturato, riattiva i clienti abituali, rispondi alle recensioni su Google e TheFork. IA in italiano, prezzi in euro, prova 14 giorni.',
    keywords: 'software per ristoranti italia, software gestione marketing ristorante, fidelizzazione clienti ristorante, rispondere recensioni google ristorante',
    badge: 'Pensato per i ristoratori italiani',
    h1: 'Il software che recupera fatturato al tuo ristorante in Italia',
    heroSub:
      "In Italia la ristorazione vive di stagionalità, coperti e passaparola digitale: una recensione ignorata su TheFork o Google pesa più di qualsiasi pubblicità. Copilote AI Business analizza i tuoi dati ogni settimana, individua dove il locale perde fatturato — clienti abituali spariti, recensioni senza risposta, servizi vuoti — e prepara le azioni per recuperarlo.",
    ctaPrimary: 'Inizia la prova gratuita (14 giorni)',
    ctaSecondary: 'Guarda la demo (90 s)',
    ctaScore: 'Ottieni il mio punteggio gratuito',
    ctaMicrocopy: 'Senza carta di credito · Disdici quando vuoi · Prezzi in euro',
    painsTitle: 'Dove perde fatturato un ristorante italiano ogni mese?',
    painsSub: 'Stime tipiche per un locale da 40–80 coperti in Italia.',
    pains: [
      { loss: 2500, title: 'Clienti abituali mai ricontattati', desc: 'Il cliente che non torna dopo 30 giorni raramente riceve un invito. Il copilota lo individua e prepara il messaggio di ritorno.' },
      { loss: 1200, title: 'Recensioni Google e TheFork senza risposta', desc: 'In Italia le recensioni decidono la prenotazione. Ogni recensione negativa ignorata svuota tavoli.' },
      { loss: 3500, title: 'Servizi e stagioni vuoti', desc: 'Martedì sera deserti, bassa stagione: senza dati, la promozione giusta parte sempre troppo tardi.' },
    ],
    featuresTitle: 'Cosa fa il copilota per il tuo locale',
    features: [
      { title: 'Audit settimanale del mancato incasso', desc: 'Ogni lunedì una diagnosi in euro: dove perdi, quanto, e quale azione conviene lanciare.' },
      { title: 'Riattivazione dei clienti abituali', desc: 'Campagne di ritorno scritte dall’IA in italiano, inviate al momento giusto — con la tua approvazione.' },
      { title: 'Gestione delle recensioni', desc: 'Bozze di risposta professionali per ogni recensione, nel tono della tua casa. Pubblichi solo dopo la tua conferma.' },
      { title: 'Promozioni anti-tavoli-vuoti', desc: 'Offerte mirate per riempire i servizi deboli, calibrate sulla stagionalità italiana.' },
    ],
    platformsNote:
      'I tuoi clienti ti cercano su Google, TheFork e TripAdvisor e ordinano con Glovo o Deliveroo: il copilota ti aiuta a trasformare quel pubblico in clienti diretti e fedeli.',
    faqTitle: 'Domande frequenti dei ristoratori italiani',
    faqs: [
      { q: 'Qual è il miglior software di marketing per un ristorante in Italia?', a: "Copilote AI Business funziona da copilota: invece di darti grafici da interpretare, l'IA individua ogni settimana le perdite di fatturato (clienti inattivi, recensioni aperte, servizi vuoti) e prepara le azioni correttive. Tu approvi, lui esegue." },
      { q: 'Quanto costa il software per ristoranti in Italia?', a: 'Tre piani in euro: Starter a 149 €/mese, Growth a 279 €/mese e Pro a 449 €/mese, dopo 14 giorni di prova gratuita senza carta di credito. Nessun vincolo di durata.' },
      { q: 'Il software è conforme al GDPR?', a: 'Sì. I dati dei tuoi clienti sono trattati secondo il GDPR: consenso, diritto alla cancellazione ed esportazione dei dati sono integrati. La base clienti resta esclusivamente tua.' },
      { q: 'È adatto a una trattoria di 30 coperti?', a: 'Sì. Il piano Starter è pensato per i locali indipendenti: bastano poche decine di clienti registrati perché audit settimanale e campagne di ritorno producano risultati misurabili.' },
      { q: 'Devo cambiare cassa o sistema di prenotazione?', a: 'No. Copilote AI Business affianca gli strumenti che già usi: analizza la base clienti e la visibilità online senza alcuna migrazione tecnica. Puoi iniziare oggi stesso.' },
    ],
    pricingTitle: 'Prezzi in euro, senza vincoli',
    pricingSub: 'Prova gratuita 14 giorni · Disdetta in un clic · IVA inclusa',
    planBullets: {
      starter: ['Audit settimanale in €', 'Fino a 500 clienti tracciati', 'Risposte alle recensioni con IA'],
      growth: ['Tutto Starter', 'Campagne di riattivazione illimitate', 'Analisi della concorrenza locale'],
      pro: ['Tutto Growth', 'Multi-sede', 'Assistenza prioritaria'],
    },
    otherMarketsTitle: 'Copilote AI Business nel tuo paese',
    finalCtaTitle: 'Ogni settimana senza copilota, incasso che sparisce',
    finalCtaSub: 'Prova gratis per 14 giorni: dal primo lunedì saprai dove il tuo ristorante perde fatturato.',
  },

  es: {
    slug: 'es',
    path: '/es',
    hreflang: 'es-ES',
    extraHreflangs: ['es'],
    locale: 'es',
    ogLocale: 'es_ES',
    currency: 'EUR',
    currencyLocale: 'es-ES',
    country: 'ES',
    countryLabel: 'España',
    flag: '🇪🇸',
    title: 'Software para restaurantes España: marketing con IA — Copilote',
    description:
      'Software para restaurantes en España: detecta pérdidas de ingresos, recupera clientes habituales y responde reseñas de Google y TheFork. IA en español, precios en euros, prueba de 14 días.',
    keywords: 'software para restaurantes españa, software marketing restaurante, fidelización clientes restaurante, responder reseñas google restaurante',
    badge: 'Diseñado para hosteleros españoles',
    h1: 'El software que recupera ingresos para tu restaurante en España',
    heroSub:
      'En España la hostelería compite en cada esquina: terrazas llenas en verano, salones vacíos entre semana y reseñas que deciden reservas. Copilote AI Business analiza tus datos cada semana, detecta dónde pierde ingresos tu local — habituales que no vuelven, reseñas sin responder, turnos flojos — y prepara las acciones para recuperarlos.',
    ctaPrimary: 'Empezar prueba gratis (14 días)',
    ctaSecondary: 'Ver la demo (90 s)',
    ctaScore: 'Obtener mi puntuación gratuita',
    ctaMicrocopy: 'Sin tarjeta · Cancela cuando quieras · Precios en euros',
    painsTitle: '¿Dónde pierde ingresos un restaurante español cada mes?',
    painsSub: 'Estimaciones típicas para un local de 40–80 comensales en España.',
    pains: [
      { loss: 2500, title: 'Habituales que no vuelven', desc: 'El cliente que no regresa en 30 días rara vez recibe un mensaje. El copiloto lo detecta y prepara la campaña de recuperación.' },
      { loss: 1200, title: 'Reseñas de Google sin responder', desc: 'En España, la nota de Google decide entre dos terrazas. Cada reseña negativa ignorada espanta reservas.' },
      { loss: 3500, title: 'Turnos flojos sin anticipar', desc: 'Martes sin gente, temporada baja: sin datos, la promoción correcta siempre llega tarde.' },
    ],
    featuresTitle: 'Lo que hace el copiloto por tu restaurante',
    features: [
      { title: 'Auditoría semanal de ingresos', desc: 'Cada lunes, un diagnóstico en euros: dónde pierdes, cuánto y qué acción lanzar.' },
      { title: 'Recuperación de clientes habituales', desc: 'Campañas de reactivación escritas por IA en español, enviadas en el momento justo — siempre con tu aprobación.' },
      { title: 'Gestión de reseñas', desc: 'Borradores de respuesta profesionales para cada reseña, con el tono de tu casa. Solo se publica con tu visto bueno.' },
      { title: 'Promociones para llenar turnos', desc: 'Ofertas dirigidas para los servicios flojos, ajustadas a la estacionalidad española.' },
    ],
    platformsNote:
      'Tus clientes te buscan en Google, TheFork y TripAdvisor y piden por Glovo: el copiloto te ayuda a convertir esa audiencia en clientes directos y fieles.',
    faqTitle: 'Preguntas frecuentes de hosteleros españoles',
    faqs: [
      { q: '¿Cuál es el mejor software de marketing para un restaurante en España?', a: 'Copilote AI Business actúa como copiloto: en lugar de darte paneles que interpretar, la IA detecta cada semana las pérdidas de ingresos (clientes inactivos, reseñas abiertas, turnos flojos) y prepara las acciones correctivas. Tú apruebas, él ejecuta.' },
      { q: '¿Cuánto cuesta el software para restaurantes en España?', a: 'Tres planes en euros: Starter a 149 €/mes, Growth a 279 €/mes y Pro a 449 €/mes, tras 14 días de prueba gratuita sin tarjeta. Sin permanencia.' },
      { q: '¿Cumple con el RGPD?', a: 'Sí. Los datos de tus clientes se tratan conforme al RGPD: consentimiento, derecho de supresión y exportación de datos integrados. La base de clientes es solo tuya.' },
      { q: '¿Sirve para un bar-restaurante pequeño en Valencia o Sevilla?', a: 'Sí. El plan Starter está pensado para locales independientes: con unas decenas de clientes registrados, la auditoría semanal y las campañas de recuperación ya dan resultados medibles.' },
      { q: '¿Tengo que cambiar mi TPV o sistema de reservas?', a: 'No. Copilote AI Business complementa tus herramientas actuales: analiza tu base de clientes y tu visibilidad online sin migración técnica. Puedes empezar hoy mismo.' },
    ],
    pricingTitle: 'Precios en euros, sin permanencia',
    pricingSub: 'Prueba gratis 14 días · Cancela en un clic · IVA incluido',
    planBullets: {
      starter: ['Auditoría semanal en €', 'Hasta 500 clientes registrados', 'Respuestas a reseñas con IA'],
      growth: ['Todo Starter', 'Campañas de reactivación ilimitadas', 'Análisis de competencia local'],
      pro: ['Todo Growth', 'Multi-local', 'Soporte prioritario'],
    },
    otherMarketsTitle: 'Copilote AI Business en tu país',
    finalCtaTitle: 'Cada semana sin copiloto, ingresos que se escapan',
    finalCtaSub: 'Prueba gratis 14 días: desde el primer lunes sabrás dónde pierde ingresos tu restaurante.',
  },

  pt: {
    slug: 'pt',
    path: '/pt',
    hreflang: 'pt-PT',
    extraHreflangs: ['pt'],
    locale: 'pt',
    ogLocale: 'pt_PT',
    currency: 'EUR',
    currencyLocale: 'pt-PT',
    country: 'PT',
    countryLabel: 'Portugal',
    flag: '🇵🇹',
    title: 'Software para restaurantes Portugal: marketing com IA — Copilote',
    description:
      'Software para restaurantes em Portugal: detete perdas de faturação, recupere clientes habituais e responda a avaliações no Google e TheFork. IA em português, preços em euros, teste de 14 dias.',
    keywords: 'software para restaurantes portugal, software marketing restaurante, fidelização clientes restaurante, responder avaliações google restaurante',
    badge: 'Criado para restauradores portugueses',
    h1: 'O software que recupera faturação para o seu restaurante em Portugal',
    heroSub:
      'De Lisboa ao Porto, a restauração portuguesa vive entre época alta de turismo e semanas vazias: as avaliações online decidem reservas e os habituais desaparecem sem aviso. O Copilote AI Business analisa os seus dados todas as semanas, deteta onde o restaurante perde faturação — clientes que não voltam, avaliações sem resposta, serviços fracos — e prepara as ações para a recuperar.',
    ctaPrimary: 'Começar teste gratuito (14 dias)',
    ctaSecondary: 'Ver a demo (90 s)',
    ctaScore: 'Obter a minha pontuação gratuita',
    ctaMicrocopy: 'Sem cartão de crédito · Cancele quando quiser · Preços em euros',
    painsTitle: 'Onde perde faturação um restaurante português todos os meses?',
    painsSub: 'Estimativas típicas para um restaurante de 40–80 lugares em Portugal.',
    pains: [
      { loss: 2500, title: 'Habituais que nunca mais voltam', desc: 'O cliente que não regressa em 30 dias raramente recebe um convite. O copiloto deteta-o e prepara a campanha de regresso.' },
      { loss: 1200, title: 'Avaliações Google sem resposta', desc: 'Em Portugal, a nota do Google decide entre dois restaurantes. Cada avaliação negativa ignorada afasta reservas.' },
      { loss: 3500, title: 'Sazonalidade não antecipada', desc: 'Época baixa, semanas vazias fora do verão: sem dados, a promoção certa chega sempre tarde demais.' },
    ],
    featuresTitle: 'O que o copiloto faz pelo seu restaurante',
    features: [
      { title: 'Auditoria semanal da faturação perdida', desc: 'Todas as segundas-feiras, um diagnóstico em euros: onde perde, quanto e que ação lançar.' },
      { title: 'Recuperação de clientes habituais', desc: 'Campanhas de reativação escritas por IA em português, enviadas no momento certo — sempre com a sua aprovação.' },
      { title: 'Gestão de avaliações', desc: 'Rascunhos de resposta profissionais para cada avaliação, no tom da sua casa. Só publica depois da sua confirmação.' },
      { title: 'Promoções para encher serviços fracos', desc: 'Ofertas dirigidas para dias fracos e época baixa, ajustadas à sazonalidade portuguesa.' },
    ],
    platformsNote:
      'Os seus clientes procuram-no no Google, TheFork e TripAdvisor e pedem pela Uber Eats ou Glovo: o copiloto ajuda-o a transformar essa audiência em clientes diretos e fiéis.',
    faqTitle: 'Perguntas frequentes de restauradores portugueses',
    faqs: [
      { q: 'Qual é o melhor software de marketing para um restaurante em Portugal?', a: 'O Copilote AI Business funciona como copiloto: em vez de painéis para interpretar, a IA deteta todas as semanas as perdas de faturação (clientes inativos, avaliações abertas, serviços fracos) e prepara as ações corretivas. O restaurador aprova, o copiloto executa.' },
      { q: 'Quanto custa o software para restaurantes em Portugal?', a: 'Três planos em euros: Starter a 149 €/mês, Growth a 279 €/mês e Pro a 449 €/mês, após 14 dias de teste gratuito sem cartão. Sem fidelização.' },
      { q: 'O software cumpre o RGPD?', a: 'Sim. Os dados dos seus clientes são tratados em conformidade com o RGPD: consentimento, direito ao apagamento e exportação de dados integrados. A base de clientes é exclusivamente sua.' },
      { q: 'Serve para uma tasca ou restaurante familiar no Porto?', a: 'Sim. O plano Starter foi pensado para restaurantes independentes: bastam algumas dezenas de clientes registados para a auditoria semanal e as campanhas de regresso darem resultados mensuráveis.' },
      { q: 'Preciso de mudar de POS ou sistema de reservas?', a: 'Não. O Copilote AI Business complementa as ferramentas que já usa: analisa a base de clientes e a visibilidade online sem qualquer migração técnica. Pode começar hoje.' },
    ],
    pricingTitle: 'Preços em euros, sem fidelização',
    pricingSub: 'Teste gratuito 14 dias · Cancele com um clique · IVA incluído',
    planBullets: {
      starter: ['Auditoria semanal em €', 'Até 500 clientes registados', 'Respostas a avaliações com IA'],
      growth: ['Tudo do Starter', 'Campanhas de reativação ilimitadas', 'Análise da concorrência local'],
      pro: ['Tudo do Growth', 'Multi-restaurante', 'Suporte prioritário'],
    },
    otherMarketsTitle: 'Copilote AI Business no seu país',
    finalCtaTitle: 'Cada semana sem copiloto é faturação perdida',
    finalCtaSub: 'Teste gratuitamente durante 14 dias: logo na primeira segunda-feira saberá onde o seu restaurante perde faturação.',
  },
};

export const MARKET_LIST = Object.values(MARKETS);

export const PLAN_ORDER = ['starter', 'growth', 'pro'];

export const PLAN_LABELS = { starter: 'Starter', growth: 'Growth', pro: 'Pro' };

export function formatPrice(amount, market) {
  const formatted = Number(amount).toLocaleString(market.currencyLocale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return market.currency === 'EUR' ? `${formatted} €` : `CHF ${formatted}`;
}

/** hreflang alternates communs à tout le cluster international. */
export function hreflangCluster() {
  const links = [];
  MARKET_LIST.forEach((m) => {
    links.push({ hreflang: m.hreflang, href: `${SITE_URL}${m.path}` });
    (m.extraHreflangs || []).forEach((h) =>
      links.push({ hreflang: h, href: `${SITE_URL}${m.path}` }));
  });
  links.push({ hreflang: 'en', href: `${SITE_URL}/` });
  links.push({ hreflang: 'x-default', href: `${SITE_URL}/` });
  return links;
}

/** JSON-LD région-aware : offres dans la devise FACTURÉE de la région (CHF Suisse / EUR marchés euro), FAQ localisée. */
export function regionJsonLd({ currency, lang, path, description, faqs = [], breadcrumbLabel }) {
  const offers = PLAN_ORDER.map((id) => ({
    '@type': 'Offer',
    name: `Copilote AI Business ${PLAN_LABELS[id]}`,
    price: String(PLAN_PRICES[id]),
    priceCurrency: currency,
    url: `${SITE_URL}${path}`,
    availability: 'https://schema.org/InStock',
  }));
  const out = [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Copilote AI Business',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      inLanguage: lang,
      url: `${SITE_URL}${path}`,
      description,
      offers,
    },
  ];
  if (faqs.length) {
    out.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  if (breadcrumbLabel) {
    out.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Copilote AI Business', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: breadcrumbLabel, item: `${SITE_URL}${path}` },
      ],
    });
  }
  return out;
}

export function jsonLdFor(market) {
  return regionJsonLd({
    currency: market.currency, lang: market.hreflang, path: market.path,
    description: market.description, faqs: market.faqs, breadcrumbLabel: market.countryLabel,
  });
}
